import React, { useState, useContext } from 'react';
import { UserContext } from '../../context/UserContext';
import FlagButton from '../FlagButton/FlagButton';
import FlagModal from '../FlagModal/FlagModal';
import KUDOS_TYPES from '../../constants/kudosTypes';
import KudoDescriptionBubble from '../KudoDescriptionBubble';
import NotificationService from '../NotificationService/NotificationService';
import './AttendeeDetailModal.css';

import MagicCircle from '../../img/icons/magic_circle.png';

// Flag reasons for attendee profiles
const FLAG_REASONS = [
  { value: '', label: 'Select a reason...' },
  { value: 'inappropriate_profile', label: 'Inappropriate profile content' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'fake_profile', label: 'Fake or impersonation profile' },
  { value: 'spam', label: 'Spam or promotional' },
  { value: 'safety_concern', label: 'Safety concern' },
  { value: 'other', label: 'Other' },
];

const AttendeeDetailModal = ({
  isOpen,
  onClose,
  attendee,
  eventId,
  kudosRemaining,
  onKudosGiven,
}) => {
  const { user } = useContext(UserContext);
  const [selectedKudos, setSelectedKudos] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredKudos, setHoveredKudos] = useState(null);

  // Flag modal state
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [isFlagSubmitting, setIsFlagSubmitting] = useState(false);
  const [hasFlagged, setHasFlagged] = useState(false);

  // Enlarged photo state
  const [showEnlargedPhoto, setShowEnlargedPhoto] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
  const getToken = () => localStorage.getItem('token');

  if (!isOpen || !attendee) return null;

  const hasGivenKudosType = (kudosType) => {
    return attendee.kudosGiven?.includes(kudosType);
  };

  // Check if ANY kudos has been given to this attendee (only one type allowed per person)
  const hasGivenAnyKudos = attendee.kudosGiven?.length > 0;

  const handleKudosClick = async (kudosType) => {
    // Only one kudos type per person allowed
    if (hasGivenAnyKudos) return;
    if (kudosRemaining <= 0) return;

    // For "Other Magic", show message input first
    if (kudosType.hasMessage && selectedKudos !== kudosType.id) {
      setSelectedKudos(kudosType.id);
      return;
    }

    // If "Other Magic" is selected and no message, don't proceed
    if (kudosType.hasMessage && !customMessage.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/user-kudos/event/${eventId}/user/${attendee._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          kudosType: kudosType.id,
          customMessage: kudosType.hasMessage ? customMessage : undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        onKudosGiven(attendee._id, kudosType.id, {
          kudosGivenCount: data.kudosGivenCount,
          kudosRemaining: data.kudosRemaining,
        });
        setSelectedKudos(null);
        setCustomMessage('');
        NotificationService.notifySuccess('We see you for seeing other people!');
        if (onClose) onClose();
      } else {
        alert(data.error || 'Failed to give kudos');
      }
    } catch (error) {
      console.error('Error giving kudos:', error);
      alert('Failed to give kudos. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle flag submission using FlagModal
  const handleFlagSubmit = async (reason, message) => {
    if (!reason) {
      alert('Please select a reason for flagging');
      return;
    }
    if (!message || message.trim().length < 10) {
      alert('Please provide additional details (at least 10 characters)');
      return;
    }

    setIsFlagSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/moderation/flag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          contentType: 'attendee',
          contentId: attendee._id,
          reason,
          message: message.trim(),
          eventId,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowFlagModal(false);
        setHasFlagged(true);
        alert('Thank you for your report. Our team will review this profile.');
      } else {
        alert(data.error || 'Failed to submit flag');
      }
    } catch (error) {
      console.error('Error flagging attendee:', error);
      alert('Failed to submit flag. Please try again.');
    } finally {
      setIsFlagSubmitting(false);
    }
  };

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="adm-close" onClick={onClose}>
          <i className="bi bi-x-lg"></i>
        </button>

        {/* Flag button - using FlagButton component (only for real users, not mock attendees) */}
        {!attendee._id?.toString().startsWith('mock_') && (
          <div className="adm-flag-wrapper">
            <FlagButton
              onClick={() => setShowFlagModal(true)}
              isFlagged={hasFlagged}
              size="large"
              title="Report this profile"
            />
          </div>
        )}

        <div className="adm-content">
          {/* Left side - Profile */}
          <div className="adm-profile-section">
            <div className="adm-avatar-wrapper">
              <img src={MagicCircle} alt="" className="adm-magic-circle" />
              <div
                className={`adm-avatar ${attendee.profilePicture ? 'clickable' : ''}`}
                onClick={() => attendee.profilePicture && setShowEnlargedPhoto(true)}
                title={attendee.profilePicture ? 'Click to enlarge' : ''}
              >
                {attendee.profilePicture ? (
                  <img src={attendee.profilePicture} alt={attendee.fullName} />
                ) : (
                  <span className="adm-initial">
                    {attendee.fullName?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
            </div>

            <div className="adm-profile-details">
              <h2 className="adm-name">{attendee.fullName || 'Unknown'}</h2>

              {/* Show message if no details available */}
              {!attendee.danceAlias && !attendee.city && !attendee.iMoveAndMuse && !attendee.eventsAttended ? (
                <p className="adm-no-details">This dancer prefers to keep their profile minimal.</p>
              ) : (
                <>
                  {/* Main details card */}
                  {(attendee.danceAlias || attendee.city || attendee.eventsAttended > 0) && (
                    <div className="adm-details-card">
                      {attendee.danceAlias && (
                        <div className="adm-detail-row">
                          <span className="adm-label">Dance Alias</span>
                          <span className="adm-value">{attendee.danceAlias}</span>
                        </div>
                      )}
                      {attendee.city && (
                        <div className="adm-detail-row">
                          <span className="adm-label">City</span>
                          <span className="adm-value">{attendee.city}</span>
                        </div>
                      )}
                      {attendee.eventsAttended > 0 && (
                        <div className="adm-detail-row">
                          <span className="adm-label">Events Attended</span>
                          <span className="adm-value">{attendee.eventsAttended}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* I Move n Muse separate card */}
                  {attendee.iMoveAndMuse && (
                    <div className="adm-quote-card">
                      <span className="adm-label">I Move n Muse</span>
                      <span className="adm-quote">"{attendee.iMoveAndMuse}"</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right side - Kudos */}
          <div className="adm-kudos-section">
            <h3 className="adm-kudos-title">Give Kudos</h3>

            <div className="adm-kudos-list">
              {KUDOS_TYPES.map((kudos) => {
                const isGiven = hasGivenKudosType(kudos.id);
                const isSelected = selectedKudos === kudos.id;

                return (
                  <div
                    key={kudos.id}
                    className="adm-kudos-item-wrapper"
                  >
                    <button
                      className={`adm-kudos-btn ${isGiven ? 'given' : ''} ${isSelected ? 'selected' : ''} ${hasGivenAnyKudos && !isGiven ? 'disabled-other' : ''}`}
                      onClick={() => handleKudosClick(kudos)}
                      disabled={hasGivenAnyKudos || kudosRemaining <= 0 || isSubmitting}
                      title={hasGivenAnyKudos && !isGiven ? 'You can only give one kudos type per person' : kudos.label}
                    >
                      <img src={kudos.icon} alt={kudos.label} />
                      {isGiven && (
                        <span className="adm-kudos-badge">
                          <i className="bi bi-check"></i>
                        </span>
                      )}
                    </button>
                    <span
                      className="adm-kudos-label"
                      onMouseEnter={() => setHoveredKudos(kudos.id)}
                      onMouseLeave={() => setHoveredKudos(null)}
                    >
                      {kudos.label}
                    </span>

                    {/* Custom message input for Other Magic */}
                    {kudos.hasMessage && isSelected && !isGiven && (
                      <div className="adm-custom-message">
                        <textarea
                          placeholder="Share what made this person special... (max 500 chars)"
                          maxLength={500}
                          value={customMessage}
                          onChange={(e) => setCustomMessage(e.target.value)}
                          autoFocus
                          rows={3}
                        />
                        <div className="adm-message-footer">
                          <span className="char-count">{customMessage.length}/500</span>
                          <button
                            className="adm-send-btn"
                            onClick={() => handleKudosClick(kudos)}
                            disabled={!customMessage.trim() || isSubmitting}
                          >
                            <i className="bi bi-send-fill"></i> Send
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {kudosRemaining <= 0 && (
              <p className="adm-no-kudos">You've used all your kudos for this event!</p>
            )}
          </div>
        </div>
      </div>

      {/* Kudos description bubble - appears on right side */}
      {hoveredKudos && (
        <KudoDescriptionBubble kudoTypeId={hoveredKudos} variant="floating" />
      )}

      {/* Enlarged Photo Modal */}
      {showEnlargedPhoto && attendee.profilePicture && (
        <div
          className="adm-enlarged-photo-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setShowEnlargedPhoto(false);
          }}
        >
          <div className="adm-enlarged-photo-container" onClick={(e) => e.stopPropagation()}>
            <button
              className="adm-enlarged-close"
              onClick={(e) => {
                e.stopPropagation();
                setShowEnlargedPhoto(false);
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <img
              src={attendee.profilePicture}
              alt={attendee.fullName}
              className="adm-enlarged-photo"
            />
            <p className="adm-enlarged-name">{attendee.fullName}</p>
          </div>
        </div>
      )}

      {/* Flag Modal with comment popup */}
      <FlagModal
        isOpen={showFlagModal}
        onClose={() => setShowFlagModal(false)}
        onSubmit={handleFlagSubmit}
        isSubmitting={isFlagSubmitting}
        alreadyFlagged={hasFlagged}
        flagReasons={FLAG_REASONS}
        contentType="profile"
      />
    </div>
  );
};

export default AttendeeDetailModal;
